import { FC, JSX, useContext, useMemo, useState } from "react";
import cx from "classnames";
import { ProviderActionsTabsEnum } from "@app/projects/network-assessment/provider-actions/enumerators";
import ProviderActionsTab from "@app/projects/network-assessment/provider-actions/ProviderActionsTab";
import { ProviderActionsDrawerContext, ProviderActionsDrawerProvider } from "@app/projects/network-assessment/provider-actions/contexts/ProviderActionsDrawerContext";
import ProcessAllChangesConfirmationModal from "@app/projects/network-assessment/provider-actions/ProcessAllChangesConfirmationModal";
import { QESButton } from "@app/shared/qes-standards/Button";
import LoadingSpinner from "@app/shared/LoadingSpinner";
import RoleRestricted from "@app/user/RoleRestricted";
import Roles from "@app/user/roles";
import { QESAlert } from "@app/shared/qes-standards/Alert";
import { FeatureFlagContext } from "@app/feature-flags/FeatureFlagContext";
import Features from "@app/feature-flags/features";
import { isOrganizationRule, ORGANIZATION_ACTIONS_UNAVAILABLE_MESSAGE } from "@app/projects/network-assessment/provider-actions/ProviderActionsDisplayFunctions";

const TAB_DISPLAY_NAMES: Record<ProviderActionsTabsEnum, string> = {
  [ProviderActionsTabsEnum.Excluded]: "Excluded",
  [ProviderActionsTabsEnum.Targeted]: "Targeted",
  [ProviderActionsTabsEnum.Terminated]: "Terminated",
};

interface WrappedProviderActionsDrawerProps {
  resetBanner?: (hasUnprocessedTerminations: boolean, hasUnprocessedTargets: boolean, hasUnprocessedExcludes?: boolean) => Promise<void>;
}

const WrappedProviderActionsDrawer: FC<WrappedProviderActionsDrawerProps> = (props): JSX.Element => {
  const actionsContext = useContext(ProviderActionsDrawerContext);
  const { enabledFeatureFlags } = useContext(FeatureFlagContext);
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
  const [shouldSkipTerminationTimeDistanceRecalc, setShouldSkipTerminationTimeDistanceRecalc] = useState(false);

  const tab = actionsContext.selectedTab;

  const loadingMessage = useMemo<string | null>(() => {
    if (!actionsContext.isAnyTabLoading) return null;

    const loaded = actionsContext.loadedTabCount;
    const total = actionsContext.totalTabCount;
    const prefix = `${loaded} of ${total} rule types loaded`;

    if (loaded === 0) {
      return `${prefix} — still loading`;
    }

    if (loaded === total - 1) {
      const pendingTab =
        !actionsContext.targetedTabState?.isInitialized ? ProviderActionsTabsEnum.Targeted
        : !actionsContext.excludedTabState?.isInitialized ? ProviderActionsTabsEnum.Excluded
        : ProviderActionsTabsEnum.Terminated;

      return `${prefix} — still loading ${TAB_DISPLAY_NAMES[pendingTab]}`;
    }

    // Multiple tabs still pending — name the one tab that has loaded.
    const loadedTab =
      actionsContext.targetedTabState?.isInitialized ? ProviderActionsTabsEnum.Targeted
      : actionsContext.excludedTabState?.isInitialized ? ProviderActionsTabsEnum.Excluded
      : actionsContext.terminatedTabState?.isInitialized ? ProviderActionsTabsEnum.Terminated
      : null;

    return loadedTab
      ? `${prefix} — ${TAB_DISPLAY_NAMES[loadedTab]} loaded, still loading the rest`
      : `${prefix} — still loading`;
  }, [
    actionsContext.excludedTabState?.isInitialized,
    actionsContext.isAnyTabLoading,
    actionsContext.loadedTabCount,
    actionsContext.targetedTabState?.isInitialized,
    actionsContext.terminatedTabState?.isInitialized,
    actionsContext.totalTabCount,
  ]);

  const handleConfirmProcessAll = () => {
    setIsConfirmModalOpen(false);
    actionsContext.processAll(shouldSkipTerminationTimeDistanceRecalc);
  };

  const showOrganizationActionsBanner = useMemo(() => {
    if (enabledFeatureFlags.has(Features.ENABLE_ORGANIZATION_PROVIDER_ACTIONS)) {
      return false;
    }
    return [actionsContext.targetedTabState, actionsContext.terminatedTabState, actionsContext.excludedTabState]
      .flatMap((tabState) => [...(tabState?.processedRules ?? []), ...(tabState?.unprocessedRules ?? [])])
      .some(isOrganizationRule);
  }, [actionsContext.excludedTabState, actionsContext.targetedTabState, actionsContext.terminatedTabState, enabledFeatureFlags]);

  const showTargetedNotification = useMemo(() => (actionsContext.unprocessedCounts?.targeted ?? 0) > 0, [actionsContext.unprocessedCounts?.targeted]);
  const showTerminatedNotification = useMemo(() => (actionsContext.unprocessedCounts?.terminated ?? 0) > 0, [actionsContext.unprocessedCounts?.terminated]);
  const showExcludedNotification = useMemo(() => (actionsContext.unprocessedCounts?.excluded ?? 0) > 0, [actionsContext.unprocessedCounts?.excluded]);

  return (
    <div className="provider-actions-drawer-container">
      <div className="d-flex justify-content-between align-items-start mb-0">
        <p className="mb-0">
          List of pending or confirmed providers, entities and their related actions within your project.
        </p>
        <div className="d-flex align-items-start">
          <QESButton
            className="as-drawer ms-3"
            disabled={actionsContext.isProcessAllDisabled}
            title={actionsContext.processAllDisabledReason ?? undefined}
            qesType="primary"
            qesTestId="provider-actions__process-changes-button"
            onClick={() => setIsConfirmModalOpen(true)}
          >
            Process All Changes
          </QESButton>
        </div>
      </div>

      {loadingMessage && (
        <div className="provider-actions-drawer__loading-indicator" data-testid="provider-actions-loading-indicator">
          <LoadingSpinner />
          <span>{loadingMessage}</span>
        </div>
      )}

      {showOrganizationActionsBanner && (
        <QESAlert type="info" persist={true} classNames="mt-3">{ORGANIZATION_ACTIONS_UNAVAILABLE_MESSAGE}</QESAlert>
      )}

      <ul className="nav nav-tabs" role="menu">
        <li className="nav-item" role="none" id="provider-actions-targeted-tab">
          {showTargetedNotification && <div className="notification" data-testid="provider-actions-targeted-notification"></div>}
          <QESButton
            className={cx({
              active: tab === ProviderActionsTabsEnum.Targeted,
              "fw-normal": true,
              "nav-link": true,
              "text-dark": true,
            })}
            onClick={() => actionsContext.setSelectedTab(ProviderActionsTabsEnum.Targeted)}
            qesTestId="provider-actions-targeted-tab-button"
            qesType="none"
            role="menuitem"
          >
            Targeted
          </QESButton>
        </li>

        <RoleRestricted allowedRoles={[Roles.TERM_PROVIDER_ADMIN, Roles.TERM_PROVIDER_VIEWER]}>
          <li className="nav-item" role="none" id="provider-actions-terminated-tab">
            {showTerminatedNotification && <div className="notification" data-testid="provider-actions-terminated-notification"></div>}
            <QESButton
              className={cx({
                active: tab === ProviderActionsTabsEnum.Terminated,
                "fw-normal": true,
                "nav-link": true,
                "text-dark": true,
              })}
              onClick={() => actionsContext.setSelectedTab(ProviderActionsTabsEnum.Terminated)}
              qesTestId="provider-actions-terminated-tab-button"
              qesType="none"
              role="menuitem"
            >
              Terminated
            </QESButton>
          </li>
        </RoleRestricted>

        <li className="nav-item" role="none" id="provider-actions-excluded-tab">
          {showExcludedNotification && <div className="notification" data-testid="provider-actions-excluded-notification"></div>}
          <QESButton
            className={cx({
              active: tab === ProviderActionsTabsEnum.Excluded,
              "fw-normal": true,
              "nav-link": true,
              "text-dark": true,
            })}
            onClick={() => actionsContext.setSelectedTab(ProviderActionsTabsEnum.Excluded)}
            qesTestId="provider-actions-excluded-tab-button"
            qesType="none"
            role="menuitem"
          >
            Excluded
          </QESButton>
        </li>
      </ul>

      {/* Mount all tabs so all 3 can load data on drawer open; only one is visible */}
      <div className="provider-actions-tab-content"
           style={{ display: tab === ProviderActionsTabsEnum.Targeted ? "flex" : "none" }}>
        <ProviderActionsTab
          tab={ProviderActionsTabsEnum.Targeted}
          tabState={actionsContext.targetedTabState}
          shouldSkipTerminationTimeDistanceRecalc={shouldSkipTerminationTimeDistanceRecalc}
          setTabState={actionsContext.setTargetedTabState}
          resetBanner={props.resetBanner}
        />
      </div>

      <div className="provider-actions-tab-content"
           style={{ display: tab === ProviderActionsTabsEnum.Terminated ? "flex" : "none" }}>
        <ProviderActionsTab
          tab={ProviderActionsTabsEnum.Terminated}
          tabState={actionsContext.terminatedTabState}
          shouldSkipTerminationTimeDistanceRecalc={shouldSkipTerminationTimeDistanceRecalc}
          setTabState={actionsContext.setTerminatedTabState}
          resetBanner={props.resetBanner}
        />
      </div>

      <div className="provider-actions-tab-content"
           style={{ display: tab === ProviderActionsTabsEnum.Excluded ? "flex" : "none" }}>
        <ProviderActionsTab
          tab={ProviderActionsTabsEnum.Excluded}
          tabState={actionsContext.excludedTabState}
          shouldSkipTerminationTimeDistanceRecalc={shouldSkipTerminationTimeDistanceRecalc}
          setTabState={actionsContext.setExcludedTabState}
          resetBanner={props.resetBanner}
        />
      </div>

      <ProcessAllChangesConfirmationModal
        isModalOpen={isConfirmModalOpen}
        isSkipSelected={shouldSkipTerminationTimeDistanceRecalc}
        isZipProject={actionsContext.isZipProject}
        setShouldSkip={setShouldSkipTerminationTimeDistanceRecalc}
        setIsModalOpen={setIsConfirmModalOpen}
        handleConfirm={handleConfirmProcessAll}
        hasUnprocessedTerminations={showTerminatedNotification}
      />

      {actionsContext.isChildFlyoutOpen && (
        <div
          className="provider-actions-drawer-overlay"
          data-testid="provider-actions-drawer-overlay"
          onClick={actionsContext.handleOverlayClick}
        />
      )}
    </div>
  );
};

const ProviderActionsDrawer: FC<WrappedProviderActionsDrawerProps> = ({resetBanner}) => {
  return (
    <ProviderActionsDrawerProvider>
      <WrappedProviderActionsDrawer resetBanner={resetBanner}/>
    </ProviderActionsDrawerProvider>
  );
};

export default ProviderActionsDrawer;